
import { Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { customValidator } from '../../../../../Utils/validator';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutesPath } from '../../../../../shared/constants/routes-path';
import { DocumentServiceType, FieldValue, ServiceName, SrActionType, SrIOName } from '../../../../../shared/constants/services.enum';
import { ApiService } from '../../../../../core/services/api.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { ApiRoot, ApiEndPoints } from '../../../../../shared/constants/api-endpoints';
import { Utils } from '../../../../../Utils/utils';
import { GetCarnetNoRequest } from '../models/get-ata-carnet-request.model';
import { CarnetQueryRequest } from '../models/carnet-query.model';
import { SrStatus } from '../../../../../shared/constants/status.enum';
import { ATARefundRequest } from '../../../../../shared/models/services-request/ata-refund.model';
import { LookupKey } from '../../../../../shared/constants/lookup-keys';
import { AtaCarnetNocLetterModel } from '../../../../../shared/models/services-request/ata-noc-letter.model';
import { SrSubmitSuccessModalComponent } from "../../../../../shared/components/sr-submit-success-modal/sr-submit-success-modal.component";
import { SkeletonLoaderComponent } from "../../../../../shared/components/skeleton-loader/skeleton-loader.component";
import { CopyCarnetSrRequest } from '../models/copy-carnet-request.model';
import { FileComponent } from '../../../../../shared/components/controls/file/file.component';
import { FolderResponse, AllowedDocumentType } from '../../../../../shared/models/documents-response.model';
import { DigitalConsentModalComponent } from "../digital-consent-modal/digital-consent-modal.component";

@Component({
  selector: 'app-noc-letter',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS, SrSubmitSuccessModalComponent, SkeletonLoaderComponent],
  templateUrl: './noc-letter.component.html',
  styleUrl: './noc-letter.component.scss'
})
export class NocLetterComponent implements OnDestroy {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  lookupKey = LookupKey;
  fieldValue = FieldValue;
  documentServiceType = DocumentServiceType;
  form: FormGroup;
  userLoginName: string = "";
  userCSN: any = "";
  srNumber: any = "";
  lang: any = "";

  loginUserDetail: any = "";
  showATAHolderType: boolean = false;
  srType: string = "";
  srSubType: string = "";
  totalSteps: number = 3;
  step: number = 1;
  loadingDoc: boolean = false;
  srInprgoressAwating: boolean = false;
  showNotes: boolean = false;

  // This method is used to initialize the class instance using fb, shared service, api, router, and route
  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.loginUserDetail = this.sharedService.getUserProfile();
    this.userLoginName = this.sharedService.getUserProfile()['Login Name'];
    this.userCSN = this.sharedService.getCsnAccountId('csn');

    this.srType = ServiceName.ATA_CARNET;
    this.srSubType = ServiceName.NOC_LETTER_CARNET;
    let userName = `${this.loginUserDetail?.['First Name']} ${this.loginUserDetail?.['Last Name']}`;
    this.form = this.fb.group({
      carnetNumber: [null],
      carnetType: ['', [Validators.required]],
      foreignCarnetNumber: ["", [Validators.maxLength(30)]],
      letterType: [null, [Validators.required, Validators.maxLength(250)]],
      fullName: [{ value: userName, disabled: true }, [Validators.required]],
      email: ['', [Validators.required, customValidator('email'), Validators.maxLength(350)]],
      countryCode: ['', Validators.required],
      mobile: ['', [Validators.required, customValidator('uaeMobile'), Validators.maxLength(40)]],
      phone: ['', [Validators.required, customValidator('mobile'), Validators.maxLength(40)]],
      agree: [false, [Validators.requiredTrue]],
      paymentMethod: [null, [Validators.required]]
    });

    this.setDefaultValue();
    // SR Number in the url
    const routeSrNumber = this.route.snapshot.params?.['sr_no'];
    const routeCsn = this.route.snapshot.params?.['csn'];
    if (routeCsn) {
      this.userCSN = routeCsn;
    }
    if (routeSrNumber) {
      this.srNumber = routeSrNumber;
    }
    this.carnetTypeChange('Local');
    // in case of draft
    if (sessionStorage.getItem("srNumber") && this.srNumber == "") {
      let storeSrNumber: any = sessionStorage.getItem("srNumber");
      this.srNumber = storeSrNumber ? storeSrNumber : "";
    }

    this.route.params.subscribe(params => {
      const srNo = params['sr_no'];
      const csn = params['csn'];
      if (csn) {
        this.userCSN = csn;
      }
      if (srNo && srNo !== this.srNumber) {
        this.srNumber = srNo;
        this.getSRDetail();
      }
    });

    if (this.srNumber) {
      this.getSRDetail();
    }

    if (!this.userCSN && !this.srNumber) {
      this.sharedService.toastMessage('INFO', 'NO_CSN_NUMBER', 'info');
      this.router.navigate([RoutesPath.DASHBOARD]);
      return;
    }

    if (!this.srNumber) {
      this.getATACarnetList();
      this.openFolder();
    }
  }
  setDefaultValue() {
    this.form.patchValue({
      countryCode: '971'
    });
    this.form.get('countryCode')?.disable();
  }
  // This method is used to handle carnet type change event using value
  carnetTypeChange(value: any) {
    const isLocal = (value === FieldValue.LOCAL || value === FieldValue.LOCAL_AR);
    this.toggleRequired(isLocal, ['carnetNumber']);
    this.toggleRequired(!isLocal, ['foreignCarnetNumber']);
  }

  // This method is used to toggle required using condition and controls
  private toggleRequired(condition: boolean, controls: string[]): void {
    Utils.addRemoveValidation(this.form, condition, controls);
  }

  carnetNumberList: any = [];
  loadingCarnetList: boolean = false;
  // This method is used to get ata carnet list
  getATACarnetList() {
    this.loadingCarnetList = true;
    const request = new GetCarnetNoRequest();
    request.body.DCLoginName = this.userLoginName;
    request.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"].CSN = this.userCSN;
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"]['SR Type'] = ServiceName.ATA_CARNET;
    request.body.DCIncomingHierarchy["ListOfDC Community Portal ATA SR IO"]["DC Community Portal ATA SR"]['Sub Type'] = ServiceName.NOC_LETTER_CARNET;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.GET_ATA_CARNET_DETAILS,
      request
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["SiebelMessage"]?.["DC Community Portal ATA SR"]) {
          let result = Utils.checkIsArray(response["SiebelMessage"]["DC Community Portal ATA SR"]);

          const validCarnets = this.sharedService.getValidCarnetList(result);
          this.carnetNumberList = [...this.carnetNumberList, ...validCarnets];
          this.loadingCarnetList = false;
        } else {
          this.loadingCarnetList = false;
        }
      }, error: () => {
        this.loadingCarnetList = false;
      }
    })
  }



  // This method is used to submit
  submit() {
    if (!this.form.get('carnetNumber')?.value && !this.form.get('foreignCarnetNumber')?.value) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isCarnetValidityNull || this.expiredCarnetMsg) {
      return;
    }
    const formFieldData = this.form.getRawValue();
    if (this.step == 1) {
      this.submitSRData(formFieldData, SrActionType.DRAFT);
      this.step = 2;
      return
    }
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    if (this.form.invalid) {
       this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      this.form.markAllAsTouched(); return;
    }

    this.submitSRData(formFieldData, SrActionType.SUBMIT);
  }

  //get folder id and attachments
  srFolder!: FolderResponse;
  mainFromSectionDocs: AllowedDocumentType[] = [];

  // This method is used to open folder
  openFolder() {
    this.loadingDoc = true;
    let isLabels = !this.srNumber; // true if srNumber is empty
    this.sharedService.getDocuments(this.srNumber, DocumentServiceType.ATA_NOC, isLabels)
      .subscribe({
        // This method is used to handle the next callback using response
        next: (response: any) => {
          if (response.success) {
            this.loadingDoc = false;
            // If SR exists → response.data, otherwise → response
            this.srFolder = response.data ? response.data : response;
            this.mainFromSectionDocs = this.srFolder.allowedDocumentTypes?.filter((d: any) => !d.forUISection);
          } else {
            this.loadingDoc = false;
            this.sharedService.toastMessage("error", response.message, 'error');
          }
        },
        // This method is used to handle the error callback using err
        error: err => {
          this.loadingDoc = false;
          this.sharedService.toastMessage("error", err, 'error');
        }
      });
  }
  // This method is used to check required documents
  checkRequiredDocuments(): boolean {
    // If there are no docs to check, nothing is missing
    if (!this.mainFromSectionDocs || this.mainFromSectionDocs.length === 0) {
      return false;
    }
    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    // ViewChildren not ready or not fully rendered yet – defer document checks
    if (fileCmps.length < this.mainFromSectionDocs.length) {
      return false;
    }

    let hasMissing = false;
    for (let index = 0; index < this.mainFromSectionDocs.length; index++) {
      const doc = this.mainFromSectionDocs[index];
      const cmp = fileCmps[index];
      // Safety guard; should not happen due to length check above
      if (!cmp) continue;

      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing((doc)?.mandatory)) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }


  expiredCarnetMsg: boolean = false;
  technicalErrorMsg: boolean = false;
  isCarnetValidityNull: boolean = false;
  // This method is used to handle carnet change event using option
  onCarnetChange(option: any) {
    this.copyCarnetErrorMsg = '';
    this.expiredCarnetMsg = false;
    this.errorMsg = "";
    this.technicalErrorMsg = false;
    this.isCarnetValidityNull = false;
    this.copyCarnetErrorMsg = "";
    if (!option?.selectedLov) {
      return;
    }
    // Need to check the carnet validity
    const validityDateStr = option?.selectedLov['DC ATA Carnet Validity Dt'];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize today's date

    if (!validityDateStr) {
      this.isCarnetValidityNull = true;
      this.technicalErrorMsg = true;
      return;
    }
    const validityDate = new Date(validityDateStr);
    validityDate.setHours(0, 0, 0, 0);
    if (isNaN(validityDate.getTime())) {
      // Invalid date format
      this.expiredCarnetMsg = true;
      return;
    }

    if (validityDate < today) {
      // Date is before today → expired
      this.expiredCarnetMsg = true;
      return;
    }

    let srNo = option?.selectedLov["SR Number"];
    if (this.srNumber && srNo) {
      this.loadingSrDetail = true;
      this.copyCarnetNoDataToSR();
    } else {
      if (srNo) {
        let formData = this.form.getRawValue();
        this.loadingSrDetail = true;
        this.submitSRData(formData, SrActionType.DRAFT, true);
      }
    }
  }
  copyCarnetErrorMsg: string = '';
  // This method is used to copy carnet no data to sr
  copyCarnetNoDataToSR() {
    let selectedCarNetSrNo: any = this.carnetNumberList?.find((c: any) => c.Value === this.form.get("carnetNumber")?.value)?.["SR Number"];

    if (!selectedCarNetSrNo) {
      this.loadingSrDetail = false;
      return;
    }
    const copyCarnetReq = new CopyCarnetSrRequest();
    copyCarnetReq.body.DCLoginName = this.userLoginName;
    copyCarnetReq.body.DCToSRNumber = this.srNumber;
    copyCarnetReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    let dcPortal = copyCarnetReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"];
    dcPortal["SR Number"] = selectedCarNetSrNo;
    dcPortal.CSN = this.userCSN;
    dcPortal['SR Type'] = this.srType;
    dcPortal['Sub Type'] = this.srSubType;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      copyCarnetReq
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["SiebelMessage"]?.["DC Community Portal SR"] && response["Error Message"] == null || response["Error Message"] == "") {
          this.step = 2;
          this.loadingSrDetail = false;
          this.openFolder();
        } else {
          this.loadingSrDetail = false;
          this.form.get("carnetNumber")?.setValue(null);
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.copyCarnetErrorMsg = response['Error Message'];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      }
    })
  }

  isFromSumit: boolean = false;
  formSubmitLoader: boolean = false;
  isSaved: boolean = false;
  successModal: boolean = false;
  showActions: boolean = true;
  loadingSrDetail: boolean = false;

  // This method is used to get sr detail
  private getSRDetail() {
    this.loadingSrDetail = true;
    const queryReq = new CarnetQueryRequest();

    queryReq.body.DCLoginName = this.userLoginName || "PRAKASHPOOJARY";
    queryReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    let portalSr = queryReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR IO"]["DC Community Portal SR"];
    portalSr["SR Number"] = this.srNumber;
    portalSr.CSN = this.userCSN || "";
    portalSr['SR Type'] = this.srType;
    portalSr['Sub Type'] = this.srSubType;

    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_QUERY_SERVICEREQUEST,
      queryReq
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        this.loadingSrDetail = false;
        if (response?.SiebelMessage && response?.SiebelMessage["DC Community Portal SR"]) {

          let srData = response?.SiebelMessage["DC Community Portal SR"];
          this.userCSN = srData?.["DC Member Num"] || this.userCSN;
          if (!this.carnetNumberList?.length && this.userCSN) {
            this.getATACarnetList();
          }
          let fields = {
            carnetNumber: srData?.["DC Carnet Number"] ?? null,
            foreignCarnetNumber: srData?.["DC Foreign Carnet Number"] ?? null,
            carnetType: srData?.["DC Carnet Type"] ?? null,
            letterType: srData?.["DC ATA NOC Letter Type"] ?? null,
            email: srData?.["DC Holder Email"] ?? null,
            countryCode: srData?.["DC Holder Mobile"] ? Utils.parseMobile(srData?.["DC Holder Mobile"], 'cc') : '971',
            mobile: srData?.["DC Holder Mobile"] ? Utils.parseMobile(srData?.["DC Holder Mobile"], 'number') : null,
            phone: srData?.["DC Holder Phone"] ?? null,
            agree: true,
            paymentMethod: srData?.["DC Payment Method"] ?? null
          }
          this.form.patchValue(fields);
          this.carnetTypeChange(srData["DC Carnet Type"]);

          this.step = 2;
          if (this.sharedService.enableNotesOption(srData["Status"])) {
            this.srInprgoressAwating = true;
            this.showNotes = true;
          }

          if (srData["Status"] != SrStatus.Draft && srData["Status"] != SrStatus.AwaitingClarification && srData["Status"] != SrStatus.Open) {
            this.showActions = false;
            this.form.disable(); // disables all fields in the form
          }
          this.openFolder();
        }
      },
      // This method is used to handle the error callback
      error: () => {
        this.loadingSrDetail = false
      }
    })
  }

  // This method is used to save and close
  saveAndClose(): void {
    const invalidControls = Utils.getInvalidControls(this.form);
    console.warn('Invalid controls:', invalidControls);
    const formFieldData = this.form.getRawValue();
    this.submitSRData(formFieldData, SrActionType.SAVE);
  }
  errorMsg: string = "";
  // This method is used to submit sr data using form data, action type, and is carnet no changed
  private submitSRData(formData: any, actionType: string, isCarnetNoChanged: boolean = false) {

    this.errorMsg = "";
    this.technicalErrorMsg = false;
    let isActionSubmit = actionType == SrActionType.SUBMIT;
    let isActionSave = actionType == SrActionType.SAVE;
    let isActionDraft = actionType == SrActionType.DRAFT;

    if (Utils.hasMaxLengthError(this.form)) {
      this.form.markAllAsTouched();
      return;
    }

    if (isActionSubmit && (this.form.invalid || this.checkRequiredDocuments() || this.noAdvancePaymentBalance)) {
       this.sharedService.toastMessage('ERROR', "FILL_REQUIRED_FIELDS_DOCUMENTS", 'error', false);
      return;
    }

    let nocLetterReq = new AtaCarnetNocLetterModel();
    nocLetterReq.body.DCLoginName = this.userLoginName;
    nocLetterReq.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    nocLetterReq.body.DCMethod = isActionSubmit ? "SUBMIT" : "SAVEASDRAFT";
    nocLetterReq.body.DCLanguage = this.lang == 'ar' ? "ARA" : "ENU";

    nocLetterReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]["SR Number"] = '1-12690154678';


    let portalSr = nocLetterReq.body.DCIncomingHierarchy["ListOfDC Community Portal SR Upsert IO"]["DC Community Portal SR"]
    portalSr["SR Number"] = this.srNumber;
    portalSr["CSN"] = this.userCSN;
    portalSr['SR Type'] = this.srType;
    portalSr['Sub Type'] = this.srSubType;
    portalSr["DC Payment Method"] = formData.paymentMethod ?? "";
    //this.lang == 'ar' ? FieldValue.PAYMENT_METHOD_ONLINE_AR : FieldValue.PAYMENT_METHOD_ONLINE_EN;
    portalSr['DC Holder Mobile'] = formData.mobile ? `${formData.countryCode}${formData.mobile}` : "";
    portalSr['DC Holder Phone'] = formData.phone ?? "";
    portalSr['DC Holder Email'] = formData.email ?? "";
    portalSr['DC Carnet Type'] = formData.carnetType ?? "";
    portalSr['DC ATA NOC Letter Type'] = formData.letterType ?? "";
    portalSr['DC Channel'] = this.lang == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN;
    if (isCarnetNoChanged == false) {
      portalSr['DC Carnet Number'] = formData.carnetNumber ?? "";
    }
    portalSr['DC Foreign Carnet Number'] = formData.foreignCarnetNumber ?? "";
    portalSr["DC ATA Consent"] = "Y";
    portalSr["DC ATA Pledged Letter Consent"] = "Y";
    portalSr["DC Bank Detail Consent"] = "N";

    if (isActionSubmit && this.expiredCarnetMsg) {
      return;
    }
    this.technicalErrorMsg = false;
    this.formSubmitLoader = !isActionDraft;
    if(!isActionDraft){
      this.showActions = false;
    }
    this.api.post(
      ApiRoot.ATA_DC_SERVICE_REQUEST_INTEGRATION_API,
      ApiEndPoints.ATA_MANAGE_SERVICE_REQUEST,
      nocLetterReq
    ).subscribe({
      // This method is used to handle the next callback using response
      next: (response: any) => {
        if ((response['Error Code'] == null || response['Error Code'] == "DC-VAL-ERR") && response["DCSRNumber"] && response["Error Message"] == null || response["Error Message"] == "") {
          this.isSaved = true;
          this.showActions = true;
          if (isActionSave || isActionDraft) {
            if (isActionDraft && this.srNumber == "") {
              this.srNumber = response["DCSRNumber"];
              sessionStorage.setItem("srNumber", response["DCSRNumber"]);
              if (isCarnetNoChanged) {
                this.copyCarnetNoDataToSR();
              }
              this.openFolder();
            } else {
              this.srNumber = response["DCSRNumber"];
              if (isActionSave) {
                this.successModal = true;
              }
            }
          } else {
            this.srNumber = response["DCSRNumber"];
            //set sr detail for success page to get ioname
            const { service, serviceAr } = Utils.getServiceLabels(this.srSubType, this.lang);
            let srDetailObj = {
              serviceSubTypeEn: service,
              serviceSubTypeAr: serviceAr,
              mainService: this.srType,
              service: this.srSubType,
              IOName: SrIOName.ISSUE_NEW_ATA_CARNET,
              srNumber: this.srNumber,
              totalSteps: this.totalSteps,
              step: this.step
            }
            this.sharedService.setSrResponse(srDetailObj);

            this.sharedService.toastMessage('SUCCESS', 'REQUEST_SUBMITTED', 'success');
            sessionStorage.removeItem("srNumber");
            this.router.navigate([`${RoutesPath.SERVICE}/${RoutesPath.SUCCESS_PAGE + "/" + this.srNumber}`]);
          }
          this.formSubmitLoader = false;
        } else {
          this.showActions = true;
          this.formSubmitLoader = false;
          if (response['Error Code'] == 'DC-VAL-ERR' && response["Error Message"] != null) {
            this.errorMsg = response["Error Message"];
          } else {
            this.technicalErrorMsg = true;
          }
        }
      },
      // This method is used to handle the error callback using e
      error: (e) => {
        this.showActions = true;
        this.formSubmitLoader = false;
        this.technicalErrorMsg = true;
      }
    })
  }
  // This method is used to calculate form progress
  get formProgress() {
    if (this.form.disabled) {
      return 100;
    }
    if (!this.form.invalid) {
      if (!this.checkRequiredDocuments()) {
        return 100;
      }
    }
    return Utils.calculateFormProgress(this.step, this.totalSteps)
  }
  // This method is used to close success modal
  closeSuccessModal() {
    this.successModal = false;
    sessionStorage.removeItem("srNumber");
    this.router.navigate([RoutesPath.DASHBOARD]);
  }
  // This method is used to open terms condition 
  isTermsCondition: boolean = false
  openTermsCondition() {
    this.isTermsCondition = true;
    const formData = this.form.getRawValue();
    this.submitSRData(formData, SrActionType.DRAFT);

    let url = `${RoutesPath.SERVICE}/${RoutesPath.ATA.TERMS}`
    this.router.navigate([url]);
  }
  // This method is used to handel file event using document and document field
  handelFileEvent(document: any, documentField: any) {

  }
  // This method is used to handle the on destroy lifecycle hook
  ngOnDestroy(): void {
    //sessionStorage.removeItem("srNumber");
  }
  // This method is used to handle payment method change event using option
  noAdvancePaymentBalance: boolean = false;
  onPaymentMethodChange(value: any) {
    this.noAdvancePaymentBalance = false;
    const selectedValue = value;
    if (selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES || selectedValue === FieldValue.PAYMENT_METHOD_ADVANCE_FOR_SERVICES_AR) {
      this.noAdvancePaymentBalance = !this.sharedService.hasSufficientAdvanceForServicesBalance();
    }else{
      this.noAdvancePaymentBalance = false;
    }
  }
}
